/**
 * NEXUMA — AccessDenied Component
 *
 * Shown when a user tries to access a route or action they don't have
 * permission for. Provides context-aware messaging and navigation.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import usePermissions from '../../hooks/usePermissions';

export default function AccessDenied({
  title       = 'Akses i ndaluar',
  description = null,
  requiredPermission = null,
  compact     = false,
}) {
  const history = useHistory();
  const { primaryRole } = usePermissions();

  const msg = description ||
    (requiredPermission
      ? `Ju nuk keni privilegjin e nevojshëm: "${requiredPermission}"`
      : 'Ju nuk keni autorizim për të parë këtë faqe.');

  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(239,68,68,0.08)' : '#fff5f5',
          border: '1px solid',
          borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(239,68,68,0.3)' : '#fecaca',
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', color: 'error.main', fontWeight: 500 }}>
          {msg}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '60vh',
        p:              3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width:    '100%',
          p:        { xs: 3, md: 5 },
          borderRadius: 3,
          border:   '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width:  72,
            height: 72,
            borderRadius: '50%',
            bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(239,68,68,0.12)' : '#fff0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 34, color: 'error.main' }} />
        </Box>

        {/* HTTP-style status */}
        <Typography
          sx={{
            fontSize:    '0.7rem',
            fontWeight:  700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color:       'text.disabled',
            mb:          0.5,
          }}
        >
          403 — Forbidden
        </Typography>

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5, lineHeight: 1.3 }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color:     'text.secondary',
            fontSize:  '0.9rem',
            lineHeight: 1.6,
            mb:        3,
          }}
        >
          {msg}
        </Typography>

        {/* Role badge */}
        {primaryRole && (
          <Box
            sx={{
              display:        'inline-flex',
              alignItems:     'center',
              gap:            0.75,
              px:             1.5,
              py:             0.5,
              borderRadius:   '20px',
              bgcolor:        primaryRole.bgColor || '#f1f5f9',
              border:         '1px solid',
              borderColor:    primaryRole.color || '#e2e8f0',
              mb:             3,
            }}
          >
            <Box
              sx={{
                width:       8,
                height:      8,
                borderRadius: '50%',
                bgcolor:     primaryRole.color || '#64748b',
              }}
            />
            <Typography
              sx={{
                fontSize:  '0.72rem',
                fontWeight: 600,
                color:     primaryRole.color || '#64748b',
                letterSpacing: '0.03em',
              }}
            >
              {primaryRole.label}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }}
          >
            Kthehu
          </Button>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => history.push('/')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }}
          >
            Ballina
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
